/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * How registration behaves for one event.
 *
 * These four rules used to live in the register button's stored configuration
 * and were applied by the server. They are applied here now, so the page decides
 * whether entry is offered, what status a seat is written with, and whether a
 * pass is minted for it.
 *
 * READ THIS BEFORE CHANGING ANYTHING HERE. Because the decision is now made in
 * the browser, it is advice rather than enforcement: anyone able to post JSON
 * can ignore it. What that makes these rules is the LAST honest description of
 * what the product intends — so they fail closed, every time, and a case nobody
 * anticipated offers nothing rather than everything.
 *
 * Two things are deliberately NOT decided here, because the server owns them and
 * should:
 *
 *   • `event.status` — moved by scheduled transitions on a clock
 *     (PUBLISHED -> REGISTRATION_OPEN -> REGISTRATION_CLOSED -> COMPLETED).
 *     We read it; we never set it.
 *   • Capacity — a row limit on event_registration, checked inside the write
 *     under the same lock as the insert. A browser cannot enforce a cap against
 *     other people's concurrent writes, and must not pretend to.
 */

/** The only status in which entry is offered. */
export const REGISTRATION_OPEN = 'REGISTRATION_OPEN';

/**
 * The registration types, as stable codes.
 *
 * Keyed on `longcode` and never on the row id or the display name: ids differ
 * between installs, and the name is editable text an organiser can change.
 */
export const OPEN_FOR_ALL = 'OPEN_FOR_ALL';
export const INVITATION_ONLY = 'INVITATION_ONLY';
export const IMMEDIATE_REGISTRATION = 'IMMEDIATE_REGISTRATION';
export const WAITING_LIST = 'WAITING_LIST';

/** Statuses a registration row may be written with from this page. */
export const CONFIRMED = 'CONFIRMED';
export const PENDING = 'PENDING';

/**
 * A place being paid for right now. It OCCUPIES the activity's capacity —
 * `cap_registrations_per_activity` counts CONFIRMED and HOLD alike — which is
 * the whole reason it exists.
 *
 * Before it, a paid seat became a row only when the gateway's webhook landed,
 * 20 to 65 seconds after the money moved, and until then the place read as
 * free to everyone. Three people could each be charged for the same last
 * places, and only one of them could have them. A HOLD is written the instant
 * Pay is pressed, so the second and third are turned away before the payment
 * page opens rather than after their card is charged.
 *
 * It is deliberately NOT one of the statuses the page treats as an entry: a
 * hold is a reservation against other people, not a promise to the person
 * holding it, and they can still close the tab. See fetchMyRegistrations'
 * ACTIVE_STATUSES, which leaves it out.
 */
export const HOLD = 'HOLD';

/**
 * A place given back. Written when a held place will not be paid for — the
 * buyer started again, the payment failed, or the hold aged out — and it frees
 * the place because the cap counts neither CANCELLED nor PENDING.
 */
export const CANCELLED = 'CANCELLED';

/** Every code this page knows how to act on. Anything else is unknown. */
const KNOWN = new Set([
  OPEN_FOR_ALL, INVITATION_ONLY, IMMEDIATE_REGISTRATION, WAITING_LIST,
]);

/**
 * The registration type's stable code, or null when it cannot be read.
 *
 * `scope.registration_type` is the page's own data source — the event row
 * carries only `registration_type_id`, a surrogate that means nothing here.
 * Null is returned rather than a guess: every rule below treats it as "do not
 * offer entry".
 */
export function registrationLongcode(scope) {
  const source = scope && scope.registration_type;
  if (!source || typeof source !== 'object') return null;

  // The source arrives wrapped by the data-source layer as {status, data}, but
  // a caller that has already unwrapped it should work too.
  const row = source.data && typeof source.data === 'object' ? source.data : source;
  const code = row && row.longcode;
  return typeof code === 'string' && code ? code : null;
}

/**
 * Everything the page needs to know about entry for this event.
 *
 * One object rather than four separate predicates, so the button, the modal and
 * the payload builder cannot disagree with each other about the same event.
 */
export function registrationRules(event, scope) {
  const longcode = registrationLongcode(scope);
  const status = event && event.status;

  // Unknown or missing type -> nothing is offered. A code this build has never
  // heard of may be one with rules we do not implement (an approval step, a
  // ballot), and offering ordinary entry for it would be wrong in the
  // direction that writes rows.
  const known = longcode !== null && KNOWN.has(longcode);

  // Invitation only: no button, no modal, nothing sent. The invitation flow
  // does not exist yet, so there is nothing else to offer either.
  const invitationOnly = longcode === INVITATION_ONLY;

  // The window is the server's answer, not ours. We are reading the status its
  // scheduled transitions produced.
  const windowOpen = status === REGISTRATION_OPEN;

  const waitingList = longcode === WAITING_LIST;

  return {
    longcode,
    canRegister: Boolean(known && windowOpen && !invitationOnly),
    // A queued seat occupies no place, which is the whole point of a queue —
    // and it matches the capacity rule, which counts only CONFIRMED rows.
    seatStatus: waitingList ? PENDING : CONFIRMED,
    // No pass for a seat that has not got a place yet. A QR that opens the gate
    // for somebody still in the queue is worse than no QR at all.
    mintsPass: !waitingList,
    // Why entry is not offered, for the band to say something better than
    // silence. Null when it is offered.
    reason: reasonNotOffered({ known, invitationOnly, windowOpen, status }),
  };
}

function reasonNotOffered({ known, invitationOnly, windowOpen, status }) {
  if (!known) return 'unavailable';
  if (invitationOnly) return 'invitation_only';
  if (!windowOpen) {
    if (status === 'COMPLETED') return 'completed';
    if (status === 'REGISTRATION_CLOSED') return 'closed';
    return 'not_open';
  }
  return null;
}
